import { Box, Drawer, Stack } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

interface IProgressBarSegment {
  isCompleted: boolean;
}

interface IEntryCard {
  isClickable: boolean;
  isCompleted: boolean;
  isCurrentSession: boolean;
}

export const DrawerRoot = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    backgroundImage: "none",
    backgroundColor: theme.palette.dsSurface.v100,
    border: `1px solid ${alpha(theme.palette.dsBase.v200, 0.04)}`,
    borderRadius: theme.spacing(2.5),
    boxShadow: `0px 2px 24px ${alpha(theme.palette.dsBase.v100, 0.32)}`,
    color: theme.palette.dsBase.v200,
    margin: theme.spacing(1),
    overflow: "hidden",
    width: theme.spacing(57.5),
  },
}));

export const DrawerContentStack = styled(Stack)(({ theme }) => ({
  height: "100%",
  padding: theme.spacing(3),
}));

export const DrawerActionButtonBox = styled(Box)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: alpha(theme.palette.dsSurface.v200, 0.6),
  border: `1px solid ${alpha(theme.palette.dsBase.v200, 0.04)}`,
  borderRadius: theme.spacing(3),
  cursor: "pointer",
  display: "flex",
  height: theme.spacing(6),
  justifyContent: "center",
  width: theme.spacing(6),
  "&:hover": {
    backgroundColor: theme.palette.dsSurface.v300,
  },
}));

export const LoadingContainerStack = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  alignSelf: "center",
  flex: 1,
  justifyContent: "center",
  maxWidth: theme.spacing(38),
  width: "100%",
}));

export const DrawerBodyStack = styled(Stack)({
  flex: 1,
});

export const StreakBanner = styled(Box)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: alpha(theme.palette.dsOrange?.v100 ?? theme.palette.dsAmber?.v200 ?? theme.palette.dsPrimary.v300, 0.08),
  borderRadius: theme.spacing(2),
  display: "flex",
  justifyContent: "center",
  paddingBlock: theme.spacing(1.25),
  width: "100%",
}));

export const ProgressBarSegment = styled(Box, {
  shouldForwardProp: prop => prop !== "isCompleted",
})<IProgressBarSegment>(({ theme, isCompleted }) => ({
  backgroundColor: isCompleted ? theme.palette.dsGreen!.v200 : alpha(theme.palette.dsBase.v200, 0.12),
  borderRadius: theme.spacing(1.25),
  flex: 1,
  height: theme.spacing(1.25),
}));

export const PeriodCompleteMessageBox = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.dsBase.v200, 0.03),
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2.5),
  textAlign: "center",
}));

export const EntryCard = styled(Box, {
  shouldForwardProp: prop => prop !== "isClickable" && prop !== "isCompleted" && prop !== "isCurrentSession",
})<IEntryCard>(({ theme, isClickable, isCompleted, isCurrentSession }) => ({
  backgroundColor: theme.palette.dsSurface.v200,
  border: `1px solid ${isCurrentSession ? alpha(theme.palette.dsGreen!.v200, 0.4) : alpha(theme.palette.dsBase.v200, 0.04)}`,
  borderRadius: theme.spacing(2),
  cursor: isClickable ? "pointer" : "default",
  opacity: isCompleted ? 0.24 : 1,
  padding: theme.spacing(2),
  "&:hover": {
    backgroundColor: isClickable ? theme.palette.dsSurface.v300 : theme.palette.dsSurface.v200,
  },
}));

export const EntryContentStack = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(2),
}));

export const EntryTextStack = styled(Stack)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  paddingLeft: theme.spacing(1),
}));

export const EntryStatusSlot = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flex: `0 0 ${theme.spacing(4)}`,
  height: theme.spacing(4),
  justifyContent: "center",
  width: theme.spacing(4),
  paddingRight: theme.spacing(1),
}));

export const CompletedBadge = styled(Box)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.dsGreen!.v200,
  borderRadius: theme.spacing(0.75),
  display: "flex",
  height: theme.spacing(3),
  justifyContent: "center",
  width: theme.spacing(3),
}));

export const PlayButton = styled(Box)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: alpha(theme.palette.dsSurface.v200, 0.6),
  border: `1px solid ${alpha(theme.palette.dsBase.v200, 0.04)}`,
  borderRadius: "50%",
  cursor: "pointer",
  display: "flex",
  flexShrink: 0,
  height: theme.spacing(4),
  justifyContent: "center",
  width: theme.spacing(4),
  "&:hover": {
    backgroundColor: theme.palette.dsSurface.v300,
  },
}));

export const FutureSessionIndicator = styled(Box)(({ theme }) => ({
  border: `1px solid ${alpha(theme.palette.dsBase.v200, 0.24)}`,
  borderRadius: theme.spacing(0.75),
  flexShrink: 0,
  height: theme.spacing(3),
  width: theme.spacing(3),
}));
